import React from "react";
import { Link } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import "../../assets/css/departmentCard.css";

/**
 * DepartmentCard - Reusable card for department/report KPI displays.
 *
 * Props:
 * - label: Card title/label
 * - sublabel: Card description/subtitle
 * - icon: Rendered icon element for the header (e.g. <GroupIcon style={{ fontSize: 20 }} />)
 * - stat1Icon/stat2Icon/stat3Icon: Rendered icon elements for each stat box
 * - stat1Label/stat2Label/stat3Label, stat1Value/stat2Value/stat3Value: Stat box content
 * - stat1Color/stat2Color/stat3Color: CSS class for stat styling ("products", "assets", "default")
 * - qualityScore: {score, label} for the quality progress bar
 * - onClick: Callback fired on an unmodified left click
 * - linkTo: Link href (also enables cmd/ctrl-click to open in a new tab)
 * - isEmpty: Disables the card when true
 * - recentlyUsed, rls: Badge flags
 */
const formatStatValue = (value) =>
  typeof value === "string" && Number.isNaN(Number(value))
    ? value
    : parseInt(value || 0, 10).toLocaleString();

export const DepartmentCard = ({
  label,
  sublabel,
  icon,
  qualityScore,
  onClick,
  linkTo = "#",
  isEmpty = false,
  recentlyUsed = false,
  rls = false,
  stat1Icon,
  stat1Label,
  stat1Value,
  stat1Color = "products",
  stat2Icon,
  stat2Label,
  stat2Value,
  stat2Color = "assets",
  stat3Icon,
  stat3Label,
  stat3Value,
  stat3Color = "default",
}) => {
  const clickable = !isEmpty;

  const qScore = qualityScore?.score || 0;
  const barColor =
    qScore > 95 ? "#27a35a" : qScore < 90 ? "#e0476b" : "#d98300";

  const handleClick = (e) => {
    if (isEmpty) {
      e.preventDefault();
      return;
    }
    const isModifiedClick =
      e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
    if (isModifiedClick) return;
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Link
      to={isEmpty ? "#" : linkTo || "#"}
      onClick={handleClick}
      className={`pp-card dp-card ${clickable ? "clickable" : "disabled"}`}
    >
      {/* Card Head */}
      <div className="pp-card-head">
        {icon && <div className="pp-card-ico">{icon}</div>}
        <div className="pp-card-ttl">
          <Tooltip title={label} placement="top">
            <h3 className="pp-card-title">{label}</h3>
          </Tooltip>
          <p className="pp-card-desc" title={sublabel}>
            {sublabel || "Not Available"}
          </p>
        </div>
        {recentlyUsed && (
          <span className="pp-recently-used">
            <AccessTimeIcon style={{ fontSize: 11 }} /> Recently
          </span>
        )}
        {rls && <span className="pp-rls-badge">RLS</span>}
        {clickable && (
          <ChevronRightIcon style={{ fontSize: 18 }} className="pp-card-arrow" />
        )}
      </div>

      {/* Stats Section */}
      {(stat1Value !== undefined ||
        stat2Value !== undefined ||
        stat3Value !== undefined) && (
        <div className="pp-card-stats">
          {stat1Value !== undefined && (
            <div className="pp-mini">
              {stat1Icon && (
                <div className={`pp-mini-ico ${stat1Color}`}>{stat1Icon}</div>
              )}
              <div>
                <div className="pp-mini-lbl">{stat1Label}</div>
                <div
                  className={`pp-mini-val ${
                    typeof stat1Value === "string" ? "pp-mini-val-small" : ""
                  }`}
                >
                  {formatStatValue(stat1Value)}
                </div>
              </div>
            </div>
          )}

          {stat2Value !== undefined && (
            <div className="pp-mini">
              {stat2Icon && (
                <div className={`pp-mini-ico ${stat2Color}`}>{stat2Icon}</div>
              )}
              <div>
                <div className="pp-mini-lbl">{stat2Label}</div>
                <div
                  className={`pp-mini-val ${
                    typeof stat2Value === "string" ? "pp-mini-val-small" : ""
                  }`}
                >
                  {formatStatValue(stat2Value)}
                </div>
              </div>
            </div>
          )}

          {stat3Value !== undefined && (
            <div className="pp-mini">
              {stat3Icon && (
                <div className={`pp-mini-ico ${stat3Color}`}>{stat3Icon}</div>
              )}
              <div>
                <div className="pp-mini-lbl">{stat3Label}</div>
                <div
                  className={`pp-mini-val ${
                    typeof stat3Value === "string" ? "pp-mini-val-small" : ""
                  }`}
                >
                  {formatStatValue(stat3Value)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quality Score Section */}
      {qualityScore && (
        <div className="pp-card-q">
          <div className="pp-card-q-top">
            <span className="lbl">{qualityScore.label || "Quality Score"}</span>
            <span className="val" style={{ color: barColor }}>
              {qualityScore.score}
            </span>
          </div>
          <div className="pp-card-q-track">
            <i style={{ width: `${qScore}%`, background: barColor }} />
          </div>
        </div>
      )}
    </Link>
  );
};

export default DepartmentCard;
